import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import DenyAdmissionModal from 'components/modals/admission/DenyAdmissionModal';
import DeleteAdmissionSummaryModal from 'components/modals/DeleteAdmissionSummaryModal';
import EditAdmissionPatientModal from 'components/modals/EditAdmissionPatientModal';
import CircularProgress from 'components/shared/CircularProgress';
import Foldout from 'components/shared/Foldout';
import Tabs, { TabType } from 'components/Tabs';
import { colors } from 'styles/theme/colors';

import AdmissionCriteriaTab from './AdmissionCriteriaTab';
import AdmissionFilesTab from './AdmissionFilesTab';
import AdmissionLoadingState from './AdmissionLoadingState';
import AdmissionSlideoutHeader from './AdmissionSlideoutHeader';
import AdmissionSummaryTab from './AdmissionSummaryTab';
import useAdmissionSlideout from './useAdmissionSlideout';

export default function AdmissionSlideout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { admissionId, admission, showFullLoading, handleInitialLoadingDone, attachmentsCount } =
    useAdmissionSlideout();

  useEffect(() => {
    setIsOpen(true);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => navigate('/admissions'), 500);
  };

  const tabs = [
    { value: 'criteria', label: 'Admission Criteria', to: `/admissions/${admissionId}/criteria` },
    { value: 'summary', label: 'Summary', to: `/admissions/${admissionId}/summary` },
    {
      value: 'files',
      label: attachmentsCount !== undefined ? `Files (${attachmentsCount})` : 'Files',
      to: `/admissions/${admissionId}/files`,
    },
  ] as const satisfies readonly TabType[];

  const activeTab = tabs.find((tab) => location.pathname.endsWith(`/${tab.value}`))?.value ?? 'criteria';

  const closeButton = <CloseButton onClick={handleClose}>&times;</CloseButton>;

  return (
    <>
      <Foldout isNested open={isOpen} width='min(100%, 1200px)' onBackdropClick={handleClose}>
        <Container>
          {!admission ? (
            <LoadingContainer>
              {closeButton}
              <SpinnerContainer>
                <CircularProgress color={colors.primaryBlue} />
              </SpinnerContainer>
            </LoadingContainer>
          ) : showFullLoading ? (
            <>
              <HeaderCloseOnly>{closeButton}</HeaderCloseOnly>
              <Content>
                <AdmissionLoadingState admission={admission} onLoadingFinished={handleInitialLoadingDone} />
              </Content>
            </>
          ) : (
            <>
              <AdmissionSlideoutHeader
                admission={admission}
                onDeny={() => setShowDenyModal(true)}
                onEditPatient={() => setShowEditPatientModal(true)}
                onDeleteSummary={() => setShowDeleteModal(true)}
                closeButton={closeButton}
              />

              <TabBar>
                <Tabs tabs={tabs} />
              </TabBar>

              <Content>
                {admission && activeTab === 'criteria' && <AdmissionCriteriaTab admission={admission} />}
                {admission && activeTab === 'summary' && <AdmissionSummaryTab admission={admission} />}
                {admission && activeTab === 'files' && <AdmissionFilesTab reviewId={admission.id} />}
              </Content>
            </>
          )}
        </Container>
      </Foldout>

      {admission && showDenyModal && (
        <ModalWrapper>
          <DenyAdmissionModal admission={admission} onCancel={() => setShowDenyModal(false)} />
        </ModalWrapper>
      )}

      {admission && showEditPatientModal && (
        <ModalWrapper>
          <EditAdmissionPatientModal admission={admission} onCancel={() => setShowEditPatientModal(false)} />
        </ModalWrapper>
      )}

      {admission && showDeleteModal && (
        <ModalWrapper>
          <DeleteAdmissionSummaryModal admission={admission} onCancel={() => setShowDeleteModal(false)} />
        </ModalWrapper>
      )}
    </>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  position: relative;
`;

const SpinnerContainer = styled.div`
  background-color: ${colors.black25};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  width: 100px;
`;

const HeaderCloseOnly = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px 0;
  position: relative;
`;

const CloseButton = styled.button`
  all: unset;
  color: ${colors.black75};
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
  position: absolute;
  right: 24px;
  top: 16px;

  &:hover {
    color: ${colors.black};
  }
`;

const TabBar = styled.div`
  // Pairs with 10px top padding on each tab item for 24px total spacing
  padding: 14px 40px 0;
  border-bottom: 1px solid ${colors.black10};
`;

const Content = styled.div`
  flex: 1;
  padding: 24px 40px 40px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const ModalWrapper = styled.div`
  position: relative;
  z-index: 1200;
`;
